/**
 * PromptVoiceField — textarea with an integrated mic that transcribes the
 * audio into the existing prompt via /api/transcribe (OpenAI Whisper).
 *
 * Reuses the same `useVoiceRecorder` hook + `RecordingBar` used in the
 * chat input, so the UX is 1:1 with what the manager already knows.
 *
 * - While idle: shows the textarea as-is, with a small mic button pinned
 *   at the bottom-right (inside the textarea container).
 * - While recording / uploading: the mic row morphs into the RecordingBar
 *   (Listening… + waveform + mm:ss + ✕ cancel / ✓ confirm), pinned below
 *   the textarea. The textarea itself stays visible and editable.
 * - On confirm: transcription text is appended to the existing value via
 *   the onChange callback. NOTHING is auto-sent.
 *
 * Drop-in: just wrap your textarea JSX with <PromptVoiceField
 *   value={prompt} onChange={setPrompt}>{textareaEl}</PromptVoiceField>
 */
import { Mic } from 'lucide-react';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import RecordingBar from '@/components/shared/RecordingBar';

export default function PromptVoiceField({
  value,
  onChange,
  children,          // the <textarea /> element
  testIdPrefix = 'prompt-voice',
  hint = 'Conversación sobre producción de video y contenido con IA para Ruido.ai.',
  language = 'es',
  maxSeconds = 300,
}) {
  const voice = useVoiceRecorder({
    language,
    prompt: hint,
    maxSeconds,
    onTranscribed: (text) => {
      const next = value ? `${value.trimEnd()} ${text}` : text;
      onChange(next);
    },
  });

  return (
    <div
      className={`relative rounded-xl transition-all duration-200 ${
        voice.isActive ? 'ring-1 ring-emerald-400/50 shadow-[0_4px_18px_rgba(16,185,129,0.12)]' : ''
      }`}
      data-testid={`${testIdPrefix}-wrap`}
    >
      {children}
      {voice.isActive ? (
        <div className="px-1.5 pt-2 pb-1">
          <RecordingBar
            testIdPrefix={testIdPrefix}
            phase={voice.isUploading ? 'uploading' : 'recording'}
            levels={voice.levels}
            seconds={voice.seconds}
            maxSeconds={voice.maxSeconds}
            onConfirm={voice.confirm}
            onCancel={voice.cancel}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={voice.start}
          disabled={voice.isRequesting}
          data-testid={`${testIdPrefix}-mic-btn`}
          title="Hablar (transcribir con IA)"
          aria-label="Hablar (transcribir con IA)"
          className="absolute bottom-2 right-2 p-1.5 rounded-full bg-white/80 backdrop-blur text-gray-400 hover:text-gray-700 border border-gray-200/50 transition-colors active:scale-[0.95] disabled:opacity-50"
        >
          <Mic className="w-[15px] h-[15px]" />
        </button>
      )}
      {voice.notice && (
        <div
          data-testid={`${testIdPrefix}-notice`}
          className="mt-1.5 text-[11px] text-amber-600"
        >
          {voice.notice}
        </div>
      )}
    </div>
  );
}
